import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, map } from 'rxjs/operators';
import { SchoolActivityInstance } from '../../../models';
import { InstanceTimes, SchoolActivityService } from '../../../services/school-activity.service';
import { ToastService } from '../../../services/toast.service';
import { GetSchoolActivitiesFailureAction, UpdateSchoolActivitySuccessAction } from '../../school-activities/actions/school-activities.actions';
import {
	CreateSchoolActivityInstancesBulkAction,
	CreateSchoolActivityInstancesBulkFailureAction,
	CreateSchoolActivityInstancesBulkSuccessActionV2,
	GetSchoolActivityInstancesForActivityAction,
	GetSchoolActivityInstancesForActivitySuccessAction,
	GetSchoolActivityInstancesForFlexPeriodAction,
	GetSchoolActivityInstancesSuccessAction,
	RemoveSchoolActivityInstanceAction,
	RemoveSchoolActivityInstanceFailureAction,
	RemoveSchoolActivityInstanceSuccessAction,
} from '../actions/school-activities-instances.actions';

@Injectable()
export class SchoolActivityInstancesEffects {
	loadSchoolActivityInstancesForFlexPeriod$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(GetSchoolActivityInstancesForFlexPeriodAction),
			exhaustMap(({ req }) => {
				return this.schoolActivityService.GetActivityInstancesByPeriodHTTP(req).pipe(
					map((instances: SchoolActivityInstance[]) => GetSchoolActivityInstancesSuccessAction({ instances })),
					catchError((error) => of(GetSchoolActivitiesFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	loadInstancesWhenActivityUpdates$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(UpdateSchoolActivitySuccessAction),
			filter(({ fromLiveUpdate }) => !fromLiveUpdate),
			map(({ schoolActivity }) => GetSchoolActivityInstancesForActivityAction({ activityId: schoolActivity.id }))
		);
	});

	getSchoolActivityInstancesForActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(GetSchoolActivityInstancesForActivityAction),
			concatMap(({ activityId }) => {
				return this.schoolActivityService.GetActivityInstancesHTTP({ activity_id: activityId }).pipe(
					map((instances) => ({
						activityId,
						instances,
					}))
				);
			}),
			map((props) => GetSchoolActivityInstancesForActivitySuccessAction(props))
		);
	});

	createActivityInstancesBulk$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(CreateSchoolActivityInstancesBulkAction),
			exhaustMap(({ instanceReqs }) => {
				if (instanceReqs.length === 0) {
					return of(CreateSchoolActivityInstancesBulkSuccessActionV2({ instances: [] }));
				}

				const instances: InstanceTimes[] = instanceReqs.map((req) => {
					return {
						start: req.start,
						end: req.end,
					};
				});

				const activityId = instanceReqs[0].activityId;
				return this.schoolActivityService.CreateActivityInstanceBulkHTTP(instances, activityId).pipe(
					map((instances) => CreateSchoolActivityInstancesBulkSuccessActionV2({ instances })),
					catchError((error) => of(CreateSchoolActivityInstancesBulkFailureAction({ error: error.message })))
				);
			})
		);
	});

	removeActivityInstance$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(RemoveSchoolActivityInstanceAction),
			exhaustMap(({ instanceId }) => {
				return this.schoolActivityService.DeleteActivityInstanceHTTP(instanceId).pipe(
					map(() => {
						this.toastService.openToast({ title: `Session removed`, type: 'success' });
						return RemoveSchoolActivityInstanceSuccessAction({ instanceId });
					}),
					catchError((error) => {
						this.toastService.openToast({
							title: `Issue encountered while deleting session, try again`,
							type: 'error',
						});
						return of(RemoveSchoolActivityInstanceFailureAction({ error: error.message }));
					})
				);
			})
		);
	});

	constructor(
		private actions$: Actions,
		private schoolActivityService: SchoolActivityService,
		private toastService: ToastService,
		private featureFlagService: FeatureFlagService
	) {}
}
